import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { DownloadSimple, FilePdf, FileDoc } from 'phosphor-react';
import { toast } from 'sonner';
import { downloadBlob, useEstatuto } from '../../../../hooks/useEstatuto';

interface EstatutoPdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  estatutoId: number;
  titulo: string;
}

export const EstatutoPdfModal: React.FC<EstatutoPdfModalProps> = ({
  isOpen,
  onClose,
  estatutoId,
  titulo,
}) => {
  const { downloadDocumento } = useEstatuto();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [notFilled, setNotFilled] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    let revoked = false;
    let blobUrl: string | null = null;

    const load = async () => {
      setLoading(true);
      setErrorMsg(null);
      setNotFilled(false);
      try {
        const blob = await downloadDocumento(estatutoId, 'pdf');
        // Garante o MIME application/pdf: se o backend devolver octet-stream
        // (ou type vazio), o iframe não renderiza inline e fica em branco.
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        blobUrl = URL.createObjectURL(pdfBlob);
        if (!revoked) setPdfUrl(blobUrl);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 409) {
          setNotFilled(true);
          setErrorMsg('O documento ainda não foi preenchido pelo cliente.');
        } else if (status === 503) {
          setErrorMsg(
            'Conversão para PDF temporariamente indisponível. Você ainda pode baixar o DOCX.',
          );
        } else {
          setErrorMsg('Erro ao carregar o documento.');
        }
      } finally {
        if (!revoked) setLoading(false);
      }
    };

    load();

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setPdfUrl(null);
    };
    // downloadDocumento é recriado a cada render (vem do useEstatuto), então não
    // pode entrar nas deps — senão o efeito re-executa em loop e o cleanup revoga
    // o blob URL logo após criá-lo, deixando o iframe em branco.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, estatutoId]);

  const safeTitulo = (titulo || `estatuto-${estatutoId}`)
    .normalize('NFD')
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .slice(0, 80);

  const handleDownload = async (formato: 'pdf' | 'docx') => {
    const setter = formato === 'pdf' ? setDownloadingPdf : setDownloadingDocx;
    setter(true);
    try {
      const blob = await downloadDocumento(estatutoId, formato);
      // Constituição completa: os 5 .docx vêm num zip.
      downloadBlob(blob, `${safeTitulo}.${blob.type.includes('zip') ? 'zip' : formato}`);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('Documento ainda não preenchido pelo cliente');
      } else if (status === 503 && formato === 'pdf') {
        toast.error('Conversão PDF temporariamente indisponível — tente o DOCX');
      } else {
        toast.error(`Erro ao baixar ${formato.toUpperCase()}`);
      }
    } finally {
      setter(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent borderRadius="0" m={0} maxW="100vw" maxH="100vh" h="100vh">
        <ModalHeader
          fontSize="md"
          fontWeight="700"
          color="gray.800"
          borderBottomWidth="1px"
          borderColor="gray.200"
          py={3}
          pr={14}
        >
          <Flex align="center" justify="space-between" gap={4}>
            <Text noOfLines={1} flex={1}>
              {titulo || `Estatuto #${estatutoId}`}
            </Text>
            <Flex gap={2}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FileDoc size={16} weight="bold" />}
                onClick={() => handleDownload('docx')}
                isLoading={downloadingDocx}
              >
                Baixar DOCX
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<DownloadSimple size={16} weight="bold" />}
                onClick={() => handleDownload('pdf')}
                isLoading={downloadingPdf}
              >
                Baixar PDF
              </Button>
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0} bg="gray.100">
          {loading && (
            <Flex h="full" align="center" justify="center" direction="column" gap={3}>
              <Spinner size="xl" color="blue.500" thickness="3px" />
              <Text fontSize="sm" color="gray.500">
                Carregando documento...
              </Text>
            </Flex>
          )}
          {!loading && errorMsg && (
            <Flex h="full" align="center" justify="center" direction="column" gap={3}>
              <FilePdf size={48} color="#94A3B8" weight="duotone" />
              <Text fontSize="sm" color="gray.600" maxW="400px" textAlign="center">
                {errorMsg}
              </Text>
              {!notFilled && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => handleDownload('docx')}
                  isLoading={downloadingDocx}
                >
                  Baixar DOCX
                </Button>
              )}
            </Flex>
          )}
          {!loading && !errorMsg && pdfUrl && (
            <Box as="iframe" src={pdfUrl} title="Estatuto" w="full" h="full" border="0" />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
